export class HorizontalScrollObserver {
    private rootElement: HTMLElement | null;
    private scrollElement: HTMLElement | null;
    private onWheel: ((event: WheelEvent) => void) | null;

    constructor() {
        this.rootElement = null;
        this.scrollElement = null;
        this.onWheel = null;
    }

    observe(element: HTMLElement) {
        this.dispose();

        this.rootElement = element;
        this.scrollElement = this.resolveScrollElement(element);

        if (!this.scrollElement)
            return;

        this.onWheel = (event: WheelEvent) => {
            if (!this.scrollElement)
                return;

            // If user scrolls horizontally with touchpad, let browser handle it.
            if (Math.abs(event.deltaY) <= Math.abs(event.deltaX))
                return;

            if (this.scrollElement.scrollWidth <= this.scrollElement.clientWidth)
                return;

            event.preventDefault();

            this.scrollElement.scrollTo({
                left: this.scrollElement.scrollLeft + event.deltaY,
                behavior: "auto"
            });
        };

        this.scrollElement.addEventListener("wheel", this.onWheel, { passive: false });

        requestAnimationFrame(() => this.scrollToSelected("auto"));
        setTimeout(() => this.scrollToSelected("auto"), 0);
        setTimeout(() => this.scrollToSelected("smooth"), 100);
    }

    scrollToSelected(behavior: ScrollBehavior = "smooth") {
        if (!this.scrollElement)
            return;

        const selected = this.getSelectedElement();

        if (!selected)
            return;

        this.scrollToElement(selected, behavior);
    }

    dispose() {
        if (this.scrollElement && this.onWheel) {
            this.scrollElement.removeEventListener("wheel", this.onWheel);
        }

        this.rootElement = null;
        this.scrollElement = null;
        this.onWheel = null;
    }

    private resolveScrollElement(element: HTMLElement): HTMLElement | null {
        const candidates: HTMLElement[] = [
            ...Array.from(element.querySelectorAll<HTMLElement>(
                [
                    ".application-menu.elevation header",
                    ".application-menu.elevation",
                    ".application-menu.dropdown.mud-appbar",
                    ".application-menu.dropdown",
                    ".mud-appbar",
                    "header",
                    ".mud-toolbar"
                ].join(", ")
            )),
            element
        ];

        const scrollable = candidates.find(x =>
            x.scrollWidth > x.clientWidth &&
            this.hasHorizontalOverflow(x)
        );

        return scrollable ?? candidates.find(x => x.scrollWidth > x.clientWidth) ?? candidates[0] ?? null;
    }

    private hasHorizontalOverflow(element: HTMLElement): boolean {
        const overflowX = getComputedStyle(element).overflowX;

        return (
            overflowX === "auto" ||
            overflowX === "scroll" ||
            overflowX === "overlay"
        );
    }

    private getSelectedElement(): HTMLElement | null {
        if (!this.scrollElement)
            return null;

        return (
            this.scrollElement.querySelector<HTMLElement>("a.selected") ||
            this.scrollElement.querySelector<HTMLElement>("a.active") ||
            this.scrollElement.querySelector<HTMLElement>(".mud-menu.active") ||
            this.scrollElement.querySelector<HTMLElement>(".mud-button.active") ||
            this.scrollElement.querySelector<HTMLElement>(".selected") ||
            this.scrollElement.querySelector<HTMLElement>(".active")
        );
    }

    private scrollToElement(element: HTMLElement, behavior: ScrollBehavior) {
        if (!this.scrollElement)
            return;

        const containerRect = this.scrollElement.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        const elementCenter =
            elementRect.left -
            containerRect.left +
            this.scrollElement.scrollLeft +
            elementRect.width / 2;

        const targetLeft =
            elementCenter - this.scrollElement.clientWidth / 2;

        this.scrollElement.scrollTo({
            left: Math.max(0, targetLeft),
            behavior
        });
    }
}